import React, { useState, useEffect } from 'react';
import { getPopularCountries, getCountryByCode, searchCountries, Country, COUNTRIES } from '../../data/countryCodes';
import { useLanguage } from '../../contexts/LanguageContext';

interface PhoneInputProps {
  countryCode: string;
  phoneNumber: string;
  onCountryCodeChange: (countryCode: string) => void;
  onPhoneNumberChange: (phoneNumber: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  error?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  countryCode,
  phoneNumber,
  onCountryCodeChange,
  onPhoneNumberChange,
  label,
  placeholder,
  required = false,
  disabled = false,
  className = '',
  error
}) => {
  const { t, language } = useLanguage();
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');

  // Initialize with Kuwait as default if no country code provided
  useEffect(() => {
    if (countryCode) {
      const country = getCountryByCode(countryCode) || 
                    allCountries.find(c => c.dialCode === countryCode);
      setSelectedCountry(country || null);
    } else {
      // Default to Kuwait
      const kuwait = getCountryByCode('KW');
      setSelectedCountry(kuwait || null);
      if (kuwait) {
        onCountryCodeChange(kuwait.dialCode);
      }
    }
  }, [countryCode]);

  // Get all countries for filtering
  const allCountries = COUNTRIES;

  // Filter countries based on search term
  const displayCountries = countrySearchTerm.trim() 
    ? searchCountries(countrySearchTerm)
    : [...getPopularCountries(), 
       ...allCountries.filter(country => !country.popular)];

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    onCountryCodeChange(country.dialCode);
    setShowCountryDropdown(false);
    setCountrySearchTerm('');
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, ''); // Only allow numbers
    onPhoneNumberChange(value);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.phone-input-dropdown')) {
        setShowCountryDropdown(false);
      }
    };

    if (showCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCountryDropdown]);

  return (
    <div className={`${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className={`flex ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        {/* Country Code Dropdown */}
        <div className="relative phone-input-dropdown">
          <button
            type="button"
            onClick={() => !disabled && setShowCountryDropdown(!showCountryDropdown)}
            disabled={disabled}
            className={`flex items-center px-3 py-2 border border-gray-300 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[120px] ${
              language === 'ar' ? 'rounded-r-md border-l-0' : 'rounded-l-md border-r-0'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${
              error ? 'border-red-300' : ''
            }`}
          >
            {selectedCountry ? (
              <div className="flex items-center">
                <span className="mr-1">{selectedCountry.flag}</span>
                <span className="text-sm">+{selectedCountry.dialCode}</span>
              </div>
            ) : (
              <span className="text-gray-500 text-sm">{t('selectCountry')}</span>
            )}
          </button>

          {showCountryDropdown && !disabled && (
            <div className={`absolute top-full mt-1 w-80 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-64 overflow-hidden ${
              language === 'ar' ? 'right-0' : 'left-0'
            }`}>
              {/* Search within dropdown */}
              <div className="p-2 border-b">
                <input
                  type="text"
                  placeholder={t('searchCountries')}
                  value={countrySearchTerm}
                  onChange={(e) => setCountrySearchTerm(e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              
              <div className="overflow-y-auto max-h-48">
                {displayCountries.map((country) => (
                  <button
                    key={country.code}
                    onClick={() => handleCountrySelect(country)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <span className="mr-2">{country.flag}</span>
                      <span className="text-sm">
                        {language === 'ar' ? country.nameAr : country.name}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">+{country.dialCode}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <input
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          placeholder={placeholder || t('enterPhoneNumber')}
          required={required}
          disabled={disabled}
          className={`flex-1 px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
            language === 'ar' ? 'rounded-l-md' : 'rounded-r-md'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''} ${
            error ? 'border-red-300' : ''
          }`}
          dir="ltr"
        />
      </div>
      
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default PhoneInput;